import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import cn from 'classnames';
import { FC, Fragment } from 'react';
import { Loader } from '../../components/loader/loader';
import { Pagination } from '../../components/pagination/pagination';
import { TableColumnWidget } from './table-column-widget/table-column-widget';
import { TableRow } from './table-row/table-row';
import s from './table.module.css';
import { TableProps, useTable } from './use-table';
import { Switch } from '../../components/switch/switch';

export const Table: FC<TableProps> = (props) => {
  const {
    className,
    cols,
    onEmpty,
    other,
    frontPagination,
    rows,
    rowsWithPagination,
    t,
    tableId,
    columnSettings,
    isLoading,
    orderable,
    sensors,
    handleDragEnd,
    expandedRowIds,
    renderExpandedRowContent,
    renderHead,
    pagination,
    toggleWithArchive,
    withArchiveParam,
  } = useTable(props);

  const component = (
    <div className={s.component}>
      {columnSettings && (
        <div className={cn(s.head, props.header?.className)}>
          <TableColumnWidget cols={props.cols} id={tableId} />
          {props.withArchive && (
            <Switch
              isOn={withArchiveParam}
              onToggle={toggleWithArchive}
              offLabel={t('Показать удаленные')}
              onLabel={t('Скрыть удаленные')}
            />
          )}
          {props.header?.children}
        </div>
      )}
      <div className={cn(s.tableComponent, className)} {...other}>
        <table className={s.table}>
          {renderHead && cols && (
            <thead>
              <tr>
                {orderable && <th style={{ textAlign: 'center' }}>#</th>}

                {Object.keys(cols).map((key) => (
                  <th key={key} style={{ width: cols[key].width, maxWidth: cols[key].maxWidth }}>
                    <div className={s.th}>
                      {cols[key].label}
                      {cols[key].filter}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rowsWithPagination?.map(({ className, rowId, ...rowData }, index) => {
              const key = (rowId || index) as string;
              const isExpanded = expandedRowIds?.includes(key);

              return (
                <Fragment key={key}>
                  <TableRow
                    className={cn(className as string, isExpanded && s.expandedRow)}
                    id={key}
                    index={index}
                    orderable={orderable}
                    columns={rowData}
                    cols={cols}
                    isExpanded={isExpanded}
                    expandable={!!renderExpandedRowContent}
                  />
                  {isExpanded && renderExpandedRowContent && (
                    <tr>
                      <td
                        colSpan={
                          orderable ? Object.keys(cols).length + 1 : Object.keys(cols).length
                        }
                        style={{
                          padding: '0 0 12px 0',
                        }}
                      >
                        {renderExpandedRowContent({ rowId, ...rowData }, index)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {isLoading && <Loader />}
        {!rows?.length && !isLoading && (
          <h2 className={s.empty}>
            {onEmpty?.title || t('Здесь пока пусто')}
            <span className={s.subtitle}>
              {onEmpty?.text || t('Для этой таблицы не найдено записей')}
            </span>
          </h2>
        )}
      </div>
      {(frontPagination || pagination) && (
        <Pagination
          pagePrefix={props.pagePrefix}
          total={rows.length}
          pagination={pagination}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      )}
    </div>
  );

  if (orderable) {
    return (
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={rowsWithPagination.map((item) => item.rowId) as number[]}
          strategy={verticalListSortingStrategy}
        >
          {component}
        </SortableContext>
      </DndContext>
    );
  }

  return component;
};
